import React, { useState, memo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { dynamic, getLocale, useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import Abnormal from '@home/components/Abnormal';
import BasicImage from '@home/components/BasicImage';
import { IPosterTemplate } from './types';
import iconSmartAi from '@/pages/aigc/assets/svg/icon-smart-ai.svg';
import iconPosterInspiration from '@/pages/aigc/assets/svg/icon-poster-inspiration.svg';
import iconPreviewSelect from '@/pages/aigc/assets/svg/icon-magical-image-reproduction-select.svg';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import './index.scss';
import { Bubble, Button } from 'tea-component';

// 动态导入 react-masonry-css，避免静态打包进主 chunk
const Masonry = dynamic({
  async loader() {
    const MasonryComponent = await import(/* webpackChunkName: "react-masonry-css" */ 'react-masonry-css');
    return MasonryComponent.default;
  },
});

interface IPosterTemplateGalleryProps {
  /** 模板列表数据 */
  templates: IPosterTemplate[];
  /** 加载状态 */
  loading: boolean;
  /** 选中"做同款"回调 */
  onMakeSame?: (template: IPosterTemplate) => void;
  /** 自定义瀑布流断点配置（默认 4/3/2 列响应式） */
  breakpointCols?: number | Record<number, number>;
  /** 自定义根节点类名 */
  className?: string;
}

/** 瀑布流响应式断点配置 */
const MASONRY_BREAKPOINTS = {
  default: 4,
  1400: 3,
  1100: 2,
};

/**
 * 单个模板卡片组件
 */
const TemplateCard = memo(({
  template,
  isEn,
  formatMessage,
  onMakeSame,
  onPreview,
}: {
  template: IPosterTemplate;
  isEn: boolean;
  formatMessage: (descriptor: { id: string; }) => string;
  onMakeSame?: (template: IPosterTemplate) => void;
  onPreview?: (template: IPosterTemplate) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMakeSame = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // 埋点：魔法海报点击率
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '700501006',
      extInfo: JSON.stringify({
        from: 'home',
        value: template.titleCn,
        magicPosterId: template.templateId,
      }),
      typeId: 'aigc',
    });
    onMakeSame?.(template);
  }, [template, onMakeSame]);

  const handlePreview = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview?.(template);
  }, [template, onPreview]);

  return (
    <div
      className="dba-poster-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="dba-poster-card-image-wrapper">
        <BasicImage
          src={isEn ? template.coverUrl_en : template.coverUrl_cn}
          alt={isEn ? template.titleEn : template.titleCn}
          className="dba-poster-card-image"
        />
        {/* Hover 蒙层 */}
        <div className={classNames('dba-poster-card-overlay', {
          'dba-poster-card-overlay-visible': isHovered,
        })}>
          <div className="dba-poster-card-overlay-bottom">
            {/* 标题区域 - 在做同款按钮上方 */}
            <div className="dba-poster-card-overlay-title">
              {isEn ? template.titleEn : template.titleCn}
            </div>
            <div className="dba-poster-card-overlay-actions">
              <button
                className="dba-poster-card-make-same-btn"
                onClick={handleMakeSame}
              >
                <img src={iconSmartAi} alt="" className="dba-poster-card-btn-icon" />
                {formatMessage({ id: 'dba_magic_poster_make_same' })}
              </button>
              <Bubble content={formatMessage({ id: 'dba_magic_poster_preview' })}>
                <button
                  className="dba-poster-card-preview-btn"
                  onClick={handlePreview}
                  title={formatMessage({ id: 'dba_magic_poster_preview' })}
                >
                  <img src={iconPreviewSelect} alt="" className="dba-poster-card-preview-icon" />
                </button>
              </Bubble>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

TemplateCard.displayName = 'TemplateCard';

/**
 * 模板预览弹窗组件 - 自定义弹层，自适应居中
 */
const TemplatePreviewModal = memo(({
  visible,
  template,
  isEn,
  formatMessage,
  onClose,
  onMakeSame,
  isCopilot,
}: {
  visible: boolean;
  template: IPosterTemplate | null;
  isEn: boolean;
  formatMessage: (descriptor: { id: string; }) => string;
  onClose: () => void;
  onMakeSame?: (template: IPosterTemplate) => void;
  isCopilot?: boolean;
}) => {
  if (!visible || !template) return null;

  const title = isEn ? template.titleEn : template.titleCn;
  // prompt 拼接 content_prompt + poster_prompt
  const contentPrompt = isEn ? template.content_prompt_en : template.content_prompt_cn;
  const posterPrompt = isEn ? template.poster_prompt_en : template.poster_prompt_cn;
  const prompt = [contentPrompt, posterPrompt].filter(Boolean).join('\n\n');

  const handleMakeSame = () => {
    onMakeSame?.(template);
    onClose();
  };

  const handleMaskClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="dba-poster-preview-mask" onClick={handleMaskClick}>
      <div className={classNames('dba-poster-preview-dialog', { 'dba-poster-preview-dialog--copilot': isCopilot })}>
        {/* 头部标题栏 */}
        <div className="dba-poster-preview-header">
          <span className="dba-poster-preview-title">
            {title}{isEn ? ' Details' : '详情'}
          </span>
          <span className="dba-poster-preview-close" onClick={onClose}>✕</span>
        </div>
        {/* 内容区域 */}
        <div className="dba-poster-preview-body">
          <div className="dba-poster-preview-modal">
            <div className="dba-poster-preview-image-wrapper">
              <BasicImage
                src={isEn ? template.coverUrl_en : template.coverUrl_cn}
                alt={title}
                className="dba-poster-preview-image"
              />
            </div>
            <div className="dba-poster-preview-prompt">
              <div className="dba-poster-preview-prompt-label">Prompt</div>
              <div className="dba-poster-preview-prompt-content">{prompt}</div>
            </div>
          </div>
        </div>
        {/* 底部按钮 */}
        <div className="dba-poster-preview-footer">
          <Button type="primary" className="dba-poster-preview-make-btn" onClick={handleMakeSame}>
            <img src={iconSmartAi} alt="" className="dba-poster-preview-btn-icon" />
            {formatMessage({ id: 'dba_magic_poster_make_same' })}
          </Button>
          <Button className="dba-poster-preview-close-btn" onClick={onClose}>
            {isEn ? 'Close' : '关闭'}
          </Button>
        </div>
      </div>
    </div>
  );
});

TemplatePreviewModal.displayName = 'TemplatePreviewModal';

/**
 * 海报灵感模板列表组件（瀑布流布局）
 */
const PosterTemplateGallery: React.FC<IPosterTemplateGalleryProps> = ({
  templates,
  loading,
  onMakeSame,
  breakpointCols,
  className,
}) => {
  const { formatMessage } = useIntl();
  const isEn = getLocale() === LANGUAGE_EN;
  const [previewTemplate, setPreviewTemplate] = useState<IPosterTemplate | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const isCopilot = className?.includes('copilot') ?? false;

  const handlePreview = useCallback((template: IPosterTemplate) => {
    setPreviewTemplate(template);
    setPreviewVisible(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewVisible(false);
    setPreviewTemplate(null);
  }, []);

  return (
    <div className={classNames('dba-poster-gallery', className)}>
      {/* 灵感标题 */}
      <div className="dba-poster-gallery-header">
        <div className="dba-poster-gallery-header-line" />
        <div className="dba-poster-gallery-header-content">
          <img src={iconPosterInspiration} alt="" className="dba-poster-gallery-header-icon" />
          <span className="dba-poster-gallery-header-text">
            {formatMessage({ id: 'dba_magic_poster_inspiration_title' })}
          </span>
        </div>
        <div className="dba-poster-gallery-header-line" />
      </div>

      {/* 使用 Abnormal 组件管理 loading / 空态 / 正常态 */}
      <Abnormal
        isLoading={loading}
        isNoData={isEmpty(templates)}
        isError={false}
        component={
          <Masonry
            breakpointCols={breakpointCols || MASONRY_BREAKPOINTS}
            className="dba-poster-masonry"
            columnClassName="dba-poster-masonry-column"
          >
            {templates?.map((template) => (
              <TemplateCard
                key={template.templateId}
                template={template}
                isEn={isEn}
                formatMessage={formatMessage}
                onMakeSame={onMakeSame}
                onPreview={handlePreview}
              />
            ))}
          </Masonry>
        }
      />

      {/* 预览弹窗 - 使用 Portal 渲染到 body，避免被 Copilot 等容器限制 */}
      {ReactDOM.createPortal(
        <TemplatePreviewModal
          visible={previewVisible}
          template={previewTemplate}
          isEn={isEn}
          formatMessage={formatMessage}
          onClose={handleClosePreview}
          onMakeSame={onMakeSame}
          isCopilot={isCopilot}
        />,
        document.body,
      )}
    </div>
  );
};

export default memo(PosterTemplateGallery);
